/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Stack, TextField, Button, Dialog, DialogTitle, Typography, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, IconButton } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect, useContext, ChangeEvent } from "react";

import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../layouts/AuthorizedLayout';
import { IAccountGuiModel } from '../../../model/IAccountGuiModel';
import { digiAccActorType } from '../../../common/TextTypes';
import { ISourceDocumentLineGuiModel } from '../../../model/ISourceDocumentGuiModel';
import { Clear } from '@mui/icons-material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import intervalToDuration from 'date-fns/intervalToDuration';
import { addMonths, endOfMonth } from 'date-fns';
import { RoundNumAccounting } from '../../../common/CustomFunctions';
import { toast } from 'react-toastify';
import DialogAccrualAccountSearch from './DialogAccrualAccountSearch';

interface IDialogProps {
    open: boolean,
    line: ISourceDocumentLineGuiModel,
    index: number,
    handleDocumentAddLines: (newLines: ISourceDocumentLineGuiModel[], index?: number) => void,
    closeDialog: (reason?: "backdropClick" | "escapeKeyDown") => void,
}
interface IAccrualGuiModel {
    periodFrom: Date | null,
    periodTo: Date | null,
    periods: number | undefined,
    account: IAccountGuiModel | null,
    accountError?: boolean, 
    amount?: number,
    accountField: digiAccActorType,
}

const DialogVoucherRegistrationDialogAccrual = (props: IDialogProps): JSX.Element => {

    const theme = useTheme();

    const { open, line, closeDialog, index } = props;

    const { t, i18n } = useTranslation();

    const user = useContext(UserContext);

    const [loader, setLoader] = useState(false);

    const [dateFromError, setDateFromError] = useState<string | null>(null);
    const [dateToError, setDateToError] = useState<string | null>(null);

    const getInitialAmount = (): number | undefined => {
        if (line.debitVat) {
            if (line.debitVat.rate && line.amount) {
                return RoundNumAccounting(line.amount / (1 + (line.debitVat.rate/100)),2);
            } else {
                return line.amount;
            }
        } else if (line.creditVat) {
            if (line.creditVat.rate && line.amount) {
                return RoundNumAccounting(line.amount / (1 + (line.creditVat.rate/100)),2);
            } else {
                return line.amount;
            }
        } else {
            return line.amount;
        }
    }
    const getAmount = (field: digiAccActorType): number | undefined => {
        if (field === "Supplier") {
            if (line.debitVat && line.debitVat.rate && line.amount) {
                return RoundNumAccounting(line.amount / (1 + (line.debitVat.rate / 100)),2);
            } else {
                return line.amount;
            }
        } else {
            if (line.creditVat && line.creditVat.rate && line.amount) {
                return RoundNumAccounting(line.amount / (1 + (line.creditVat.rate / 100)),2);
            } else {
                return line.amount;
            }
        }
    }

    const createLines = () => {

        if (dateFromError !== "" && dateFromError) {
            toast.error(t("voucherRegistration.errorWithDates"));
        } else if (dateToError !== "" && dateToError) {
            toast.error(t("voucherRegistration.errorWithDates"));
        } else if (accrual.amount && accrual.periods && accrual.account && !accrual.accountError) {

            let periodAmount = RoundNumAccounting(accrual.amount / accrual.periods, 2);
            let periods = accrual.periods;

            let newLines: ISourceDocumentLineGuiModel[] = [];

            for (let i = 0; i < periods; i++) {

                let newLine: ISourceDocumentLineGuiModel = {
                    ...line,
                    debitAccount: accrual.accountField === "Supplier" ? accrual.account : line.creditAccount,
                    debitAccountError: accrual.accountField === "Supplier" ? accrual.accountError : line.creditAccountError,
                    creditAccount: accrual.accountField === "Supplier" ? line.debitAccount : accrual.account,
                    creditAccountError: accrual.accountField === "Supplier" ? line.debitAccountError : accrual.accountError,
                    debitVat: undefined,
                    debitVatError: false,
                    creditVat: undefined,
                    creditVatError: false,
                    amount: periodAmount,
                    currencyAmount: undefined,
                    currencyDate: null,
                    currencyRate: undefined,
                    currencyVismaId: undefined,
                    valueDate: endOfMonth(addMonths(accrual.periodFrom as Date, i))
                }
                newLines.push(newLine);
            }

            props.handleDocumentAddLines(newLines, index);
            props.closeDialog();


        } else {
            toast(t("voucherRegistration.errorWithFields"));
        }
    }

    const [accrual, setAccrual] = useState<IAccrualGuiModel>({
        periodFrom: new Date(),
        periodTo: addMonths(new Date(),1),
        periods: 2,
        amount: getInitialAmount(),
        account: null,
        accountField: line.debitVat ? "Supplier" : "Customer",
    });

    const truncateToDate = (date: Date): Date => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    };

    const accrualUpdatePeriodFrom = (dateFrom: Date | null) => {
        if (dateFrom && accrual.periodTo) {
            const normalizedFrom = truncateToDate(dateFrom);
            const normalizedTo = truncateToDate(accrual.periodTo);

            let periods = 0;

            if (normalizedFrom.getTime() === normalizedTo.getTime()) {
                periods = 1;
            } else if (normalizedFrom < normalizedTo) {
                periods = intervalToDuration({ start: normalizedFrom, end: normalizedTo }).months! + 1;
            }

            setAccrual({
                ...accrual,
                periodFrom: dateFrom,
                periods,
            });
        }
    };

    const accrualUpdatePeriodTo = (dateTo: Date | null) => {
        if (dateTo && accrual.periodFrom) {
            const normalizedFrom = truncateToDate(accrual.periodFrom);
            const normalizedTo = truncateToDate(dateTo);

            let periods = 0;

            if (normalizedTo.getTime() === normalizedFrom.getTime()) {
                periods = 1;
            } else if (normalizedTo > normalizedFrom) {
                periods = intervalToDuration({ start: normalizedFrom, end: normalizedTo }).months! + 1;
            }

            setAccrual({
                ...accrual,
                periodTo: dateTo,
                periods,
            });
        }
    };

    const accrualUpdateAccount = (account: IAccountGuiModel | null, error: boolean) => {

        setAccrual({
            ...accrual,
            account: account,
            accountError: error
        })
    }

    const accrualUpdateAccountField = (accountField: digiAccActorType) => {
        setAccrual({
            ...accrual,
            amount: getAmount(accountField),
            accountField: accountField
        })
    }

    const clearAcrual = () => {
        setAccrual({
            periodFrom: null,
            periodTo: null,
            periods: undefined,
            account: null,
            accountField: "Supplier"
        })
    };

    //#######################################################################
    //#######################################################################
    //#######################################################################
    return <Dialog disableEnforceFocus
        maxWidth="sm"
        fullWidth
        onClose={(event, reason) => props.closeDialog(reason)}
        open={props.open}
    >
        <DialogTitle>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid>
                    <Typography variant="h6">{t("voucherRegistration.accrualOf") + " " + t("voucherRegistration.invoiceNo") + ": " + (line.invoiceNo ? line.invoiceNo : t("basic.missing")) }</Typography>
                </Grid>
                <Grid>
                    <IconButton onClick={() => closeDialog("escapeKeyDown")} color="warning"><Clear /></IconButton>
                </Grid>
            </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 0 }}>
            <Stack direction="column" spacing={4} padding={theme.spacing(4)}>
                <TextField
                    disabled
                    InputLabelProps={{ shrink: true, }}
                    size="small"
                    variant="outlined"
                    autoComplete="off"
                    color="warning"
                    value={accrual.amount}
                    label={t("voucherRegistration.accrualAmount")}
                    fullWidth
                    id="accrualAmountId" />
                <TextField
                    disabled
                    InputLabelProps={{ shrink: true, }}
                    size="small"
                    variant="outlined"
                    autoComplete="off"
                    color="warning"
                    value={accrual.periods}
                    label={t("voucherRegistration.numberOfPeriods")}
                    fullWidth
                    id="numberOfPeriodsId" />
                <TextField
                    disabled
                    InputLabelProps={{ shrink: true, }}
                    size="small"
                    variant="outlined"
                    autoComplete="off"
                    color="warning"
                    value={accrual.amount && accrual.periods ? RoundNumAccounting(accrual.amount / accrual.periods, 2) : undefined}
                    label={t("voucherRegistration.periodAmount")}
                    fullWidth
                    id="periodAmountId" />
                <DesktopDatePicker
                    label={t("voucherRegistration.periodFrom") }
                    views={['year', 'month']}
                    maxDate={accrual.periodTo}
                    value={accrual.periodFrom}
                    onChange={(newValue, context) => accrualUpdatePeriodFrom(newValue)}
                    onError={(error, contex) => { setDateFromError(error) }} />
                
                <DesktopDatePicker
                    label={t("voucherRegistration.periodTo")}
                    views={['year','month']}
                    minDate={accrual.periodFrom }
                    value={accrual.periodTo}
                    onChange={(newValue, context) => accrualUpdatePeriodTo(newValue)}
                    onError={(error, contex) => { setDateToError(error) }} />

                <FormControl color="warning" size="small" fullWidth>
                    <InputLabel id="select-actorType">{t("basic.actorType")}</InputLabel>
                    <Select color="warning"
                        labelId="select-actorType-label"
                        id="select-actorType"
                        value={accrual.accountField}
                        label={t("basic.actorType")}
                        onChange={(event) => accrualUpdateAccountField(event.target.value as digiAccActorType)}
                    >
                        <MenuItem value={"Supplier"}>{t("basic.supplier")}</MenuItem>
                        <MenuItem value={"Customer"}>{t("basic.customer")}</MenuItem>
                    </Select>
                </FormControl>
                <DialogAccrualAccountSearch account={accrual.account} handleUpdate={accrualUpdateAccount} />
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button variant="contained" onClick={createLines}>{t("basic.save")}</Button>
        </DialogActions>
    </Dialog>
}
export default DialogVoucherRegistrationDialogAccrual;